import React, { useState, useEffect } from 'react';
import { Box, Typography, Modal, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, CircularProgress, Pagination, Alert, Grid } from '@mui/material';
import { useTheme } from '@mui/material';
import { tokens } from '../theme';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import Header from '../components/Header';
import { apiGet } from '../utils/apiUtils';

const PAGE_SIZE = 10;

const AppointmentPage = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [appointments, setAppointments] = useState([]);
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  useEffect(() => {
    const fetchAppointments = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await apiGet('/api/appointment/getAll', { page, pageSize: PAGE_SIZE });
        // API should return { result: { data: [...], totalRecords, totalPages } }
        setAppointments(res.result?.data || []);
        setTotalPages(res.result?.totalPages || 1);
        setTotalRecords(res.result?.totalRecords || 0);
      } catch (err) {
        setError('Failed to fetch appointments');
      } finally {
        setLoading(false);
      }
    };
    fetchAppointments();
  }, [page]);

  const handleView = (row) => {
    setSelected(row);
    setOpen(true);
  };
  const handleClose = () => setOpen(false);
  const handlePageChange = (event, value) => setPage(value);

  return (
    <Box m="20px">
      <Header title="Appointments" subtitle="View and manage appointments" />
      <Typography variant="subtitle1" mb={2} fontWeight="bold">
        Total Appointments: {totalRecords}
      </Typography>
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <>
          <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 2 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ background: colors.redAccent[600] }}>
                  <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>ID</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Date Slot</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Name</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Contact Number</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Address</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Call Slot</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {appointments.map((row) => (
                  <TableRow key={row.Call_ID}>
                    <TableCell>{`R${row.Call_ID}`}</TableCell>
                    <TableCell>{row.Date_Slot}</TableCell>
                    <TableCell>{row.Name}</TableCell>
                    <TableCell>{row.Contact_Number}</TableCell>
                    <TableCell>{row.Address}</TableCell>
                    <TableCell>{row.Call_Slot}</TableCell>
                    <TableCell>
                      <IconButton onClick={() => handleView(row)}>
                        <VisibilityOutlinedIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box display="flex" justifyContent="flex-end" mt={2}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              shape="rounded"
              showFirstButton
              showLastButton
            />
          </Box>
        </>
      )}
      <Modal open={open} onClose={handleClose}>
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: '#fff',
          color: '#111',
          p: { xs: 2, sm: 4 },
          borderRadius: 2,
          minWidth: { xs: '90vw', sm: 400, md: 600 },
          maxWidth: '95vw',
          width: { xs: '90vw', sm: 400, md: 600 },
          maxHeight: '90vh',
          overflowY: 'auto',
        }}>
          <Typography variant="h6" mb={2}>Appointment Details</Typography>
          {selected && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography fontWeight="bold">ID</Typography>
                <Typography mb={2}>{`R${selected.Call_ID}`}</Typography>
                <Typography fontWeight="bold">Date Slot</Typography>
                <Typography mb={2}>{selected.Date_Slot}</Typography>
                <Typography fontWeight="bold">Name</Typography>
                <Typography mb={2}>{selected.Name}</Typography>
                <Typography fontWeight="bold">Contact Number</Typography>
                <Typography mb={2}>{selected.Contact_Number}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography fontWeight="bold">Address</Typography>
                <Typography mb={2}>{selected.Address}</Typography>
                <Typography fontWeight="bold">Call Slot</Typography>
                <Typography mb={2}>{selected.Call_Slot}</Typography>
                <Typography fontWeight="bold">Created</Typography>
                <Typography mb={2}>{selected.IsCreated}</Typography>
                <Typography fontWeight="bold">Updated</Typography>
                <Typography mb={2}>{selected.IsUpdated}</Typography>
              </Grid>
            </Grid>
          )}
        </Box>
      </Modal>
    </Box>
  );
};

export default AppointmentPage; 